/*
  Homepage proximity scroll-settle.

  What changed and why
  --------------------
  The previous version of this file OWNED paging: it listened for wheel and
  keydown in the CAPTURE phase, called preventDefault() +
  stopImmediatePropagation() before Lenis ever saw the event, and moved the
  page exactly one section per input. That is stricter than CSS
  `scroll-snap-type: mandatory` — there was no such thing as a position
  between two sections, so free scrolling was impossible by construction and
  a section with content across the screen (New Arrivals V2) could hold the
  page hostage until its own content was exhausted.

  This version inverts the model. Lenis owns scrolling outright, natively and
  freely, in both directions. This file only SETTLES: once scrolling has come
  to a stop, if the rest position happens to be close to a section boundary,
  it eases the last few pixels onto that boundary. If the rest position is
  not close to one, it does nothing at all and leaves the visitor parked
  wherever they chose. That "does nothing" branch is the whole difference
  between proximity and mandatory.

  Consequences worth knowing:

  - Every listener here is passive. Nothing calls preventDefault(), so wheel
    handling no longer blocks the compositor (this was a measurable INP /
    scroll-performance penalty in Lighthouse, which Shopify's theme speed
    score reads).
  - Sections are no longer required to be exactly one viewport tall. See
    assets/custom.css — they are `min-height: 100dvh` now and may grow. A
    section taller than the screen simply has its two boundaries far apart,
    so parking in its middle never triggers a settle. Nothing needs to
    declare itself as an internally-scrolling exception any more; the
    [data-snap-scroll] / [data-snap-scroll-x] opt-outs this file used to read
    are gone from the theme entirely.
  - Keyboard scrolling is not handled here at all. assets/smooth-scroll.js
    already routes the scroll keys through lenis.scrollTo(); the old capture
    listener in this file only won because it ran first. Keys now get free
    scrolling plus the same settle as everything else.
  - Desktop-only, gated on the same `min-width: 990px` as the layout it
    settles against. An earlier revision of this file ran at every breakpoint
    on the reasoning that proximity settling was safe on a phone in a way
    full-page paging never was. It cannot strand you mid-page, which is true —
    but it can still move the page under a finger that has stopped, which on
    touch reads as the page misbehaving. See the DESKTOP note below.
*/
(function () {
  // Lenis itself bails on reduced motion (smooth-scroll.js), so window.lenis
  // would never exist here anyway — but bail explicitly so the intent is
  // readable rather than incidental.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  /* Theme editor. Shopify scrolls the section a merchant selects into view
     itself, and re-renders sections underneath us on every settings change.
     A settle firing against the editor's own scroll makes the section the
     merchant just clicked slide away from them, which makes the homepage
     effectively uneditable. Cheaper and more predictable to sit this out
     entirely than to try to arbitrate — the storefront is what needs the
     polish, not the editor preview. */
  if (window.Shopify && window.Shopify.designMode) return;

  /* Fraction of the viewport within which a rest position is considered
     "close enough" to a boundary to be worth finishing. Beyond this, the
     visitor is taken to have stopped where they meant to. */
  var SETTLE_THRESHOLD_RATIO = 0.18;

  /* Quiet time with no scroll activity before a settle may fire. Long
     enough to sit out trackpad momentum and iOS rubber-banding, short
     enough not to read as a delayed lurch. Lenis emits scroll events for
     the whole of its own glide, so this timer only starts counting once the
     page has genuinely come to rest. */
  var IDLE_MS = 180;

  /* Deliberately shorter than the old 0.9s page transition: this is a nudge
     of at most SETTLE_THRESHOLD_RATIO of a screen, not a journey across
     one. */
  var SETTLE_DURATION = 0.65;

  /* Never settle within this many pixels of either end of the document.
     iOS Safari's overscroll bounce reports scroll positions past the real
     bounds, and settling into a bounce fights the browser. */
  var EDGE_DEAD_ZONE = 100;

  /* A boundary is only a settle candidate if the section it belongs to is
     at least this tall relative to the viewport. Without this rule a layout
     of short sections would have boundaries every few hundred pixels and the
     page would feel like it was grabbing at the visitor constantly. */
  var MIN_SECTION_RATIO = 0.85;

  /* Settling is DESKTOP-ONLY, and this is the same 990px breakpoint
     assets/custom.css uses — deliberately, not coincidentally.

     Everything this file does is a correction toward a section boundary, and
     section boundaries are only meaningful where the one-section-per-screen
     layout exists. That layout is entirely inside custom.css's
     `@media (min-width: 990px)` block: the `min-height: 100dvh` on each
     homepage section, the hard `height: 100dvh` on [data-fit-target]
     sections, and the snap fallback all stop applying below it. Below 990px
     the sections are natural height and the page is ordinary flowing content
     with no boundary grid to complete.

     MIN_SECTION_RATIO was assumed to cover this on its own, on the reasoning
     that natural-height mobile sections would be too short to qualify. They
     are not. This homepage is built from full-bleed media sections — the
     product hero, the tile grid, the newsletter carousel, the heat dial — and
     on a phone viewport several of them comfortably exceed 85% of the screen
     while sitting at arbitrary offsets. So boundaries did exist on mobile,
     just not on a grid, and the page would nudge itself up to 18% of a screen
     180ms after the finger left. From a dead stop, unrequested, that is the
     single most likely cause of mobile scrolling being reported as "not
     smooth": not jank, but the page appearing to move on its own.

     Read live off the MediaQueryList rather than captured as a boolean, so
     rotating a tablet or resizing a window past the breakpoint is picked up
     without a reload. `.matches` is a property read, not a re-parse, so it is
     cheap enough for the per-scroll-event path in schedule(). */
  var DESKTOP = window.matchMedia("(min-width: 990px)");

  /* Already at the target, to within this many pixels. Guards the feedback
     loop: our own lenis.scrollTo emits scroll events, which reschedule the
     idle timer, which fires once we have arrived — at which point the
     nearest boundary is the one we just landed on, zero pixels away. */
  var ARRIVED_EPSILON = 2;

  /* Ease-IN-out, unlike the easeOutExpo used everywhere else in the theme,
     and the difference matters more here than anywhere.

     Every other eased movement in this theme continues a gesture the visitor
     is already making, so a curve that starts at full speed matches the
     momentum already on screen. A settle is the opposite: it begins from a
     dead stop, some time after the visitor stopped scrolling. easeOutExpo
     from rest covers about half its distance in its first tenth of time,
     which from a standstill reads as a jolt — the page appearing to twitch
     on its own. Starting slow makes the same movement read as the page
     gently completing a thought.

     Cubic rather than something sharper: the whole travel is at most
     SETTLE_THRESHOLD_RATIO of a screen, and a steeper curve over that
     little distance is imperceptible at the start and abrupt at the end. */
  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  var idleTimer = null;

  function root() {
    return document.querySelector("main#root[data-scroll-settle]");
  }

  /* Recomputed at settle time rather than cached. It costs a handful of
     getBoundingClientRect calls once per scroll-stop — nothing next to the
     cost of being wrong after a lazy-loaded image, a font swap, a resize, or
     a Shopify section re-render changes the page's heights underneath a
     stale cache. */
  function boundaries() {
    var main = root();
    if (!main) return [];

    var viewportH = window.innerHeight;
    var scrollY = window.scrollY;
    var result = [];

    Array.prototype.forEach.call(main.children, function (el) {
      if (!el.classList.contains("shopify-section")) return;
      var rect = el.getBoundingClientRect();
      if (rect.height < viewportH * MIN_SECTION_RATIO) return;
      result.push(scrollY + rect.top);
    });

    return result;
  }

  function maxScroll() {
    return Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight
    );
  }

  function settle() {
    idleTimer = null;

    // A timer armed at desktop width can still fire after a resize or an
    // orientation change dropped us below the breakpoint.
    if (!DESKTOP.matches) return;

    var lenis = window.lenis;
    if (!lenis || lenis.isStopped) return;

    var current = window.scrollY;
    var limit = maxScroll();

    // Sitting in an overscroll bounce, or legitimately parked at an end of
    // the document. Either way there is nothing to finish.
    if (current < EDGE_DEAD_ZONE || current > limit - EDGE_DEAD_ZONE) return;

    var threshold = window.innerHeight * SETTLE_THRESHOLD_RATIO;
    var best = null;
    var bestDistance = Infinity;

    boundaries().forEach(function (top) {
      var distance = Math.abs(top - current);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = top;
      }
    });

    // The proximity branch: too far from anything to be worth correcting.
    if (best === null || bestDistance > threshold) return;

    // Already there — this is the arrival of our own previous settle.
    if (bestDistance <= ARRIVED_EPSILON) return;

    var target = Math.max(0, Math.min(limit, best));

    /* Explicit duration + easing overrides the instance's lerp mode for this
       one movement (Lenis's Animate prefers duration/easing when both are
       given). Correct here: a settle has a known start, a known end and a
       shape it should follow, which is exactly what lerp's open-ended glide
       toward a moving target cannot express. */
    lenis.scrollTo(target, {
      duration: SETTLE_DURATION,
      easing: easeInOutCubic,
    });
  }

  function schedule() {
    /* Gated here rather than at init() so the check stays live: no timer is
       ever armed below the breakpoint, and crossing it in either direction
       needs no teardown or re-wiring. Also belt-and-braces with settle()'s own
       guard, since a timer armed just before a resize could still be pending
       when it fires. */
    if (!DESKTOP.matches) {
      cancel();
      return;
    }
    window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(settle, IDLE_MS);
  }

  function cancel() {
    window.clearTimeout(idleTimer);
    idleTimer = null;
  }

  /* Input arriving mid-settle needs no special handling: Lenis treats a real
     wheel/touch event as a new, non-programmatic scroll target and drops the
     animation we started. All that is needed here is to stop the pending
     timer, which the resulting scroll events will reschedule anyway.

     The one case that must NOT schedule is input inside a nested scroller —
     the New Arrivals carousel strip, an open cart drawer, the country list.
     Those emit no window scroll of their own, but the page may still be
     drifting to a stop behind them, and settling the page out from under
     someone who is turning a carousel is exactly the kind of hijacking this
     rewrite exists to remove. [data-lenis-prevent] already marks every one
     of them (see smooth-scroll.js) — and cancelling unconditionally on any
     input covers them for free, because a nested scroller emits no window
     scroll to reschedule with. */
  ["wheel", "touchstart", "touchmove", "keydown"].forEach(function (type) {
    window.addEventListener(type, cancel, { passive: true });
  });

  window.addEventListener("resize", cancel, { passive: true });

  /* Deferred script placed after lenis.min.js/smooth-scroll.js in
     theme.liquid, so window.lenis is already set by the time this runs — but
     guard with a short retry in case that load order ever changes.

     The retry is capped. smooth-scroll.js declines to create a Lenis
     instance at all if the library failed to load, so window.lenis can be
     permanently absent — an uncapped retry would be a timer firing twenty
     times a second for the life of the page, on exactly the underpowered
     devices where the library was most likely to have failed. Giving up
     leaves the page on plain native scrolling with the CSS proximity
     fallback, which is the correct degraded state. */
  var attempts = 0;
  function init() {
    /* Touch device: smooth-scroll.js declined to create Lenis on purpose and
       stamped this class, so window.lenis is never going to appear. Skip the
       retry rather than run 40 timers over 2s on every phone page load waiting
       for something that was never coming. Deliberately NOT gated on
       DESKTOP.matches — a desktop window that starts narrow and is widened
       past the breakpoint must still end up wired, which is why the live
       gating lives in schedule() instead of here. */
    if (document.documentElement.classList.contains("smooth-scroll-off")) {
      return;
    }
    if (!window.lenis) {
      if (++attempts > 40) return; // ~2s
      window.setTimeout(init, 50);
      return;
    }
    if (!root()) return;
    window.lenis.on("scroll", schedule);
  }
  init();
})();
